import axios from 'axios'

// API Configuration  
// Use /api for production (nginx proxy) or localhost:8000 for development
const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8000' : '/api')

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
})

// Types
export interface SetupStatus {
  is_setup_complete: boolean
  message: string
}

export interface AppSetup {
  password: string
  anthropic_api_key: string
}

export interface Receipt {
  id: number
  company: string
  total_amount: number
  date: string
  payer: 'David' | 'Timna'
  is_shared: boolean
  notes?: string
}

export interface ReceiptCreate {
  company: string
  total_amount: number
  datetime: string
  payer: 'David' | 'Timna'
  is_shared?: boolean
  notes?: string
}

export interface ReceiptProcessed {
  company: string
  total_amount: number
  datetime: string
}

export interface ReportSummary {
  total_spent: number
  david_spent: number
  timna_spent: number
  difference: number
  who_owes: string
  balance_amount: number
}

export interface MonthlyEntry {
  month_id: string
  month_name: string
  david_paid: number
  timna_paid: number
  david_should_pay: number
  timna_should_pay: number
  balance_amount: number
  who_owes: string
  payments: PaymentInfo[]
  remaining_balance: number
}

export interface PaymentCreate {
  month_id: string
  payer: 'David' | 'Timna'
  amount: number
  payment_date?: string  // ISO format, optional - defaults to now
  notes?: string
}

export interface PaymentInfo {
  id: number
  month_id: string
  payment_number: number
  payer: string
  amount: number
  payment_date: string
  notes?: string
  tracking_number: string
}

// Auth storage
let authCredentials: { password: string } | null = null

export const setAuthCredentials = (password: string) => {
  authCredentials = { password }
  // Store in localStorage for persistence
  localStorage.setItem('auth', btoa(JSON.stringify({ password })))
}

export const getAuthCredentials = () => {
  if (authCredentials) return authCredentials
  
  // Try to load from localStorage
  const stored = localStorage.getItem('auth')
  if (stored) {
    try {
      authCredentials = JSON.parse(atob(stored))
      return authCredentials
    } catch (e) {
      localStorage.removeItem('auth')
    }
  }
  return null
}

export const clearAuthCredentials = () => {
  authCredentials = null
  localStorage.removeItem('auth')
}

// Request interceptor to add auth
api.interceptors.request.use((config) => {
  const creds = getAuthCredentials()
  if (creds) {
    // Use empty username with just password for Basic auth
    const token = btoa(`:${creds.password}`)
    config.headers.Authorization = `Basic ${token}`
  }
  return config
})

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthCredentials()
      window.location.reload()
    }
    return Promise.reject(error)
  }
)

// API Functions
export const apiService = {
  // Setup
  getSetupStatus: (): Promise<SetupStatus> =>
    api.get('/setup/status').then((res) => res.data),

  completeSetup: (setup: AppSetup): Promise<{ message: string }> =>
    api.post('/setup/complete', setup).then((res) => res.data),

  // Health
  healthCheck: () => api.get('/health').then((res) => res.data),

  // Receipts
  processReceipt: (file: File): Promise<ReceiptProcessed> => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/receipts/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data)
  },

  createReceipt: (receipt: ReceiptCreate, image?: File): Promise<Receipt> => {
    const formData = new FormData()
    formData.append('company', receipt.company)
    formData.append('total_amount', receipt.total_amount.toString())
    // Ensure datetime includes seconds for backend compatibility
    const datetimeWithSeconds = receipt.datetime.length === 16 ? receipt.datetime + ':00' : receipt.datetime
    formData.append('datetime_str', datetimeWithSeconds)
    formData.append('payer', receipt.payer)
    // Explicitly handle the boolean value - default to true if undefined
    const isSharedValue = receipt.is_shared === false ? 'false' : 'true'
    formData.append('is_shared', isSharedValue)
    if (receipt.notes) {
      formData.append('notes', receipt.notes)
    }
    if (image) {
      formData.append('image', image)
    }
    return api.post('/receipts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data)
  },

  getReceipts: (params?: {
    start_date?: string
    end_date?: string
    company?: string
    payer?: string
    limit?: number
    offset?: number
  }): Promise<Receipt[]> =>
    api.get('/receipts', { params }).then((res) => res.data),

  getReceipt: (id: number): Promise<Receipt> =>
    api.get(`/receipts/${id}`).then((res) => res.data),

  getReceiptImage: (id: number): Promise<Blob> =>
    api.get(`/receipts/${id}/image`, { responseType: 'blob' }).then((res) => res.data),

  updateReceipt: (id: number, receipt: ReceiptCreate): Promise<{ message: string }> => {
    const formData = new FormData()
    formData.append('company', receipt.company)
    formData.append('total_amount', receipt.total_amount.toString())
    // Ensure datetime includes seconds for backend compatibility
    const datetimeWithSeconds = receipt.datetime.length === 16 ? receipt.datetime + ':00' : receipt.datetime
    formData.append('datetime_str', datetimeWithSeconds)
    formData.append('payer', receipt.payer)
    formData.append('is_shared', (receipt.is_shared !== false).toString()) // Default to true if not specified
    if (receipt.notes) {
      formData.append('notes', receipt.notes)
    }
    return api.put(`/receipts/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data)
  },

  deleteReceipt: (id: number): Promise<{ message: string }> =>
    api.delete(`/receipts/${id}`).then((res) => res.data),

  // Stores
  getStores: (): Promise<{ stores: string[] }> =>
    api.get('/stores').then((res) => res.data),

  // Reports
  getSummaryReport: (params?: {
    start_date?: string
    end_date?: string
  }): Promise<ReportSummary> =>
    api.get('/reports/summary', { params }).then((res) => res.data),

  getMonthlyReport: (year?: number): Promise<MonthlyEntry[]> =>
    api.get('/reports/monthly', { params: year ? { year } : {} }).then((res) => res.data),

  getAvailableYears: (): Promise<{ years: number[] }> =>
    api.get('/reports/years').then((res) => res.data),

  getUnbalancedMonths: (): Promise<{ unbalanced_months: Array<{
    month_id: string,
    month_name: string,
    who_owes: string,
    balance_amount: number
  }> }> =>
    api.get('/reports/unbalanced').then((res) => res.data),

  // Payments
  createPayment: (payment: PaymentCreate): Promise<PaymentInfo> =>
    api.post('/payments', payment).then((res) => res.data),

  getPayments: (params?: {
    start_date?: string
    end_date?: string
  }): Promise<PaymentInfo[]> =>
    api.get('/payments', { params }).then((res) => res.data),

  getPaymentsForMonth: (monthId: string): Promise<PaymentInfo[]> =>
    api.get(`/payments/${monthId}`).then((res) => res.data),

  updatePayment: (paymentId: number, payment: PaymentCreate): Promise<PaymentInfo> =>
    api.put(`/payments/${paymentId}`, payment).then((res) => res.data),

  deletePayment: (paymentId: number): Promise<{ message: string }> =>
    api.delete(`/payments/${paymentId}`).then((res) => res.data),

  // Database export
  exportDatabase: (): Promise<Blob> =>
    api.get('/database/export', { responseType: 'blob' }).then((res) => res.data),

  // Admin
  updateApiKey: (apiKey: string): Promise<{ message: string }> =>
    api.put('/admin/api-key', { anthropic_api_key: apiKey }).then((res) => res.data),
}

export default api